import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeftIcon, ChartBarIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import { useTradeDetail, useUpdatePosition, useReRate } from '@/hooks/trades';
import { useTradeAccruals } from '@/hooks/trades/use-trade-accruals';
import { queryKeys } from '@/services/query-keys';
import { PageLoader } from '@/components/ui/PageLoader';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { TradeHeader } from '../components/trade-detail/TradeHeader';
import { KpiStrip } from '../components/trade-detail/kpi/KpiStrip';
import { TradeTabNavigation } from '../components/trade-detail/TradeTabNavigation';
import { useTradeTab } from '../components/trade-detail/use-trade-tab';
import { AnalyticsTab } from '../components/trade-detail/tabs/AnalyticsTab';
import { ActivityTab } from '../components/trade-detail/tabs/ActivityTab';
import { TradeTermsTab } from '../components/trade-detail/tabs/TradeTermsTab';
import { TradeActionButtons, type TradeNavItem } from '../components/trade-detail/TradeActionButtons';
import { TerminateModal } from '../components/trade-detail/modals/TerminateModal';
import { NoticeModal } from '../components/trade-detail/modals/NoticeModal';
import { SettlementDateModal } from '../components/trade-detail/modals/SettlementDateModal';
import { ProcessTradeDateModal } from '../components/trade-detail/modals/ProcessTradeDateModal';
import { AmendTradeModal } from '../components/trade-detail/modals/AmendTradeModal';
import { TradePendingBanner } from '../components/trade-detail/TradePendingBanner';
import { BoPostingPanel } from '../components/trade-detail/BoPostingPanel';
import {
  reconcileDialogCopy, SETTLE_MANUALLY_COPY,
} from '@/utils/trades/bo-posting-copy';
import { useReconcileBoPosting } from '@/hooks/trades/use-reconcile-bo-posting';
import { useRecordBoPostingFromBackoffice } from '@/hooks/trades/use-bo-posting-read-back';
import { useSettleBoPostingManually } from '@/hooks/trades/use-settle-bo-posting-manually';
import { RecordFromBackofficeDialog } from '../components/trade-detail/RecordFromBackofficeDialog';
import { useAuth } from '@/hooks/auth/use-auth';
import { ReRateModal } from '../components/trade-detail/modals/ReRateModal';
import { AssignCashPoolModal } from '../components/trade-detail/modals/AssignCashPoolModal';
import { PartialRecallModal } from '../components/PartialRecallModal';
import { PartialRecallsPanel } from '../components/PartialRecallsPanel';
import { MtmDetailsDrawer } from '../components/mtm/MtmDetailsDrawer';
import { useTradeActions } from '@/features/trades/hooks';
import { isMtmEligibleRow } from '@/utils/trades/mtm-cell-helpers';
import { useToastStore } from '@/stores';
import { formatDate } from '@/utils/format';
import { isTradeDateInFuture } from '@/utils/trades/trade-date';
import { collateralSearchForTrade } from '@/features/collateral/collateral-search';
import { collateralLegName } from '@/features/trades/hooks/trade-actions';

export function TradeDetailPage() {
  const { tradeId } = useParams({ from: '/trades/$tradeId' });
  const id = Number(tradeId);
  // LR-208 r5 M7: distinguish a non-numeric URL segment from a real "not found".
  // useTradeDetail(NaN) short-circuits via `enabled: id > 0` (no network call,
  // data=undefined, isLoading=false), so a `/trades/abc` URL otherwise fell
  // through to the generic 404 block. Support flow loses the diagnostic signal
  // "broken bookmark / deep-link" vs "real missing trade".
  const isValidId = Number.isFinite(id) && id > 0;
  const queryClient = useQueryClient();
  const { addToast } = useToastStore();
  const { hasPermission } = useAuth();
  const canTrade = hasPermission('create_trades');
  // LR-715: the first UI surface to read the `admin` capability, and LR-706 (g)'s
  // read-back pair now stands on the same gate. It has existed in
  // the vocabulary since LR-257 ("a marker for any future admin-only UI surface")
  // and the backend emits it for a superuser too (`get_user_capabilities`), so this
  // mirrors the server's own gate rather than approximating it.
  const canAdmin = hasPermission('admin');
  const reconcileBoPosting = useReconcileBoPosting();
  const recordFromBackoffice = useRecordBoPostingFromBackoffice();
  const settleBoPosting = useSettleBoPostingManually();
  const { data: trade, isLoading, error } = useTradeDetail(id);
  // Active tab persists to localStorage scoped to THIS trade id (LR-208 r5 H4)
  // so opening a different trade does not inherit the previous trade's tab.
  const [activeTab, setActiveTab] = useTradeTab(id);

  // Source accruals at the page level to feed the strip ④ (the deleted
  // AccruedIncomeHero's data path now feeds TradeVitalSigns). React Query dedupes
  // this with AnalyticsTab's own useTradeAccruals call — one network request shared.
  const { data: accruals, isLoading: accrualsLoading, isError: accrualsError } = useTradeAccruals(id);

  // ── Modal state + the page's single useTradeActions (LR-208 r3 H2) ────────
  // Hoisted out of TradeActionBar so the action buttons can move between
  // TradeHeader and StickySummary on pin transitions without tearing down an
  // in-flight dialog. One instance of each modal lives below; the buttons that
  // open them are stateless and may be unmounted/remounted freely.
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [settlementDateOpen, setSettlementDateOpen] = useState(false);
  const [settleOpen, setSettleOpen] = useState(false);
  const [settleCashOpen, setSettleCashOpen] = useState(false);
  const [settleSecuritiesOpen, setSettleSecuritiesOpen] = useState(false);
  const [terminateOpen, setTerminateOpen] = useState(false);
  // LR-711: resolving a stuck BackOffice posting. Confirmed rather than
  // fire-and-forget because it can clear a posting guard — the same rail every
  // other lifecycle mutation on this page follows.
  const [reconcileOpen, setReconcileOpen] = useState(false);
  // LR-706 (g): reading BackOffice back and writing our side from it. Confirmed
  // like every other mutation here, and with more to confirm: the dialog fetches
  // the rows first, because this is the one action that writes the ledger.
  const [recordFromBoOpen, setRecordFromBoOpen] = useState(false);
  // LR-715: closing a posting BackOffice contradicted itself about. Confirmed for
  // the same reason, and with a mandatory reason on top: it is irreversible from
  // here, and the sentence is the only evidence the settlement ever had.
  const [settleManuallyOpen, setSettleManuallyOpen] = useState(false);
  const [settleReason, setSettleReason] = useState('');
  const [partialRecallOpen, setPartialRecallOpen] = useState(false);
  // LR-478: "Mark history" peek drawer (in-place; does not leave the page).
  const [markHistoryOpen, setMarkHistoryOpen] = useState(false);
  // LR-478: amend (booked quantity) — pending trades only.
  const [amendOpen, setAmendOpen] = useState(false);
  // LR-544: manual "Open now" (fire the Trade Date action) — pending only.
  const [processTradeDateOpen, setProcessTradeDateOpen] = useState(false);
  // LR-528: re-rate (rate leg) — active / backdated matured.
  const [reRateOpen, setReRateOpen] = useState(false);
  const [assignCashPoolOpen, setAssignCashPoolOpen] = useState(false);

  // useTradeActions handles a null/undefined trade by returning an empty
  // validActions set, so it is safe to call here above the loading guard.
  // enableHolidays only while the settlement-date dialog is open. This is the
  // page's SINGLE owner of every action mutation — Notice / SettlementDate /
  // Terminate receive their mutation handles by prop and do not re-subscribe
  // (LR-208 r3 M4 — useTradeActions is now a true single source).
  const {
    validActions,
    canPerform,
    giveNotice,
    setSettlementDate,
    terminate,
    settle,
    settleCash,
    settleSecurities,
    partialRecall,
    processTradeDate,
    holidays,
    withDetailRefetch,
  } = useTradeActions(trade ?? null, settlementDateOpen);

  const navigate = useNavigate();

  // "View in MTM" shows only while the trade is VISIBLE on the MTM page (isMtmEligibleRow),
  // not merely markable: the button opens the counterparty's whole book, repo included.
  // Effective status, so a partially settled FOP trade qualifies (LR-608).
  const canViewMtm = !!trade && isMtmEligibleRow(trade);

  // LR-478: PATCH-based amend (booked quantity) for pending trades. Page-level
  // single owner, mirroring the other lifecycle mutation handles.
  const updatePosition = useUpdatePosition();
  // LR-528: re-rate (POST /re-rate/) — page-level single owner, like updatePosition.
  const reRate = useReRate();

  // Reset each settle mutation on its dialog OPEN transition only — depend on
  // the open boolean + the stable mutation.reset reference (TanStack Query
  // memoizes reset per mutation instance), so the effect fires once per open
  // and doesn't wipe a surfaced error band on an unrelated re-render.
  const settleReset = settle.reset;
  const settleCashReset = settleCash.reset;
  const settleSecuritiesReset = settleSecurities.reset;
  useEffect(() => { if (settleOpen) settleReset(); }, [settleOpen, settleReset]);
  useEffect(() => { if (settleCashOpen) settleCashReset(); }, [settleCashOpen, settleCashReset]);
  useEffect(() => { if (settleSecuritiesOpen) settleSecuritiesReset(); }, [settleSecuritiesOpen, settleSecuritiesReset]);

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.trades.detail(id) });
    // LR-208 r6 M19: also refetch the Activity / History timeline. useTradeActivity
    // has staleTime 5min + refetchOnWindowFocus false, so without this the manual
    // Refresh button (the user's explicit "reload everything" affordance) left the
    // timeline stale while detail + accruals updated — a half-refreshed page on a
    // tab whose history is legally meaningful. (Lifecycle mutations already cover
    // it via the ['positions'] prefix invalidation; only manual Refresh missed it.)
    queryClient.invalidateQueries({ queryKey: queryKeys.trades.activity(id) });
    // Invalidate the broader accruals prefix rather than a hand-assembled
    // accrualList({ position_id }) key. useTradeAccruals registers under
    // accrualList({ position_id: String(id) }), and re-deriving that exact
    // filter shape at the call site invites drift (numeric vs string id, prefix
    // changes) that would silently make the refresh refetch nothing (WR-05).
    // accrualList is a child of accruals(), so the prefix partial-matches the
    // hook's registered key and the strip ④ + accrual chart always refetch.
    queryClient.invalidateQueries({ queryKey: queryKeys.billing.accruals() });
  };

  if (!isValidId) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-20">
        <p className="text-gray-500 mb-4">
          Invalid trade ID — the URL does not point at a real position.
        </p>
        <Link to="/trades" search={{ mode: undefined, cp: undefined }} className="text-blue-600 hover:text-blue-800 flex items-center gap-1">
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Trades
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return <PageLoader />;
  }

  if (error || !trade) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-20">
        <p className="text-gray-500 mb-4">Trade not found or failed to load.</p>
        <Link to="/trades" search={{ mode: undefined, cp: undefined }} className="text-blue-600 hover:text-blue-800 flex items-center gap-1">
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Trades
        </Link>
      </div>
    );
  }

  // The leg Settle Cash posts: "Collateral" on non-cash, whose collateral leg is securities.
  const collateralLeg = collateralLegName(trade.collateral_type);

  // Action submit handlers — re-check canPerform against the freshly-loaded
  // trade (ACTN-02 / PITFALLS #7); no-op on stale / keyboard / race paths.
  const onActionSuccess = (closeDialog: () => void) =>
    withDetailRefetch(trade.id, () => {
      handleRefresh();
      closeDialog();
    });

  // Stale/race guard: when the freshly-loaded trade can no longer take the
  // action, give feedback and close the dialog rather than a silent no-op that
  // leaves an enabled Confirm button looking broken (LR-208 r6 LOW). isPending
  // bails on a fast double-click before the mutation's pending flag propagates.
  const confirmSettle = () => {
    if (settle.isPending) return;
    if (!canPerform('settle')) {
      addToast('error', 'This position changed — Settle is no longer available.');
      setSettleOpen(false);
      return;
    }
    settle.mutate({ id: trade.id }, { onSuccess: onActionSuccess(() => setSettleOpen(false)) });
  };

  // LR-711. No `canPerform` re-check: unlike the lifecycle actions this is not
  // gated on a status that another session could have moved out from under us —
  // it is gated on a posting still being unresolved, and the server answers 400 if
  // it no longer is. Re-sending under the same request id cannot double-book, so
  // the honest failure mode here is "nothing left to do", not a wrong booking.
  const confirmReconcile = () => {
    if (reconcileBoPosting.isPending) return;
    reconcileBoPosting.mutate(
      { id: trade.id },
      {
        onSuccess: (result) => {
          setReconcileOpen(false);
          handleRefresh();
          // Each outcome means something different to the person who clicked, and
          // the difference is what they do next. A single "reconciled" would be
          // wrong twice over: `unblocked` has not settled anything yet (the
          // re-post is scheduled, so the panel stays until BackOffice answers),
          // and `legacy` has not moved at all.
          const outcomes = result.outcomes ?? {};
          if ((outcomes.error ?? 0) > 0) {
            // The server answers 200 even when a block threw: the response says
            // what happened to EACH block, and one that failed is still a
            // reported outcome. Checked first, so a mixed result does not show
            // green — the operator has to know something did not happen.
            addToast(
              'error',
              'Reconciling failed on the server for at least one posting — ' +
                'nothing was changed for it. Running it again is safe; if it ' +
                'keeps failing, escalate.',
            );
          } else if ((outcomes.read_back_found ?? 0) > 0) {
            // LR-713: the search found the posting in BackOffice. That is the
            // WORSE news of the two legacy outcomes, not the better one —
            // BackOffice holds a booking our books do not, the guard is
            // deliberately still up, and settling a disagreement from a search
            // result is a decision for a person, not for this click. Checked
            // before `legacy` because a trade with both blocks should read as
            // the found one: it is the block with an answer.
            addToast(
              'error',
              'Found in BackOffice: it holds this posting, and our book does ' +
                'not. Nothing was booked or released — the trade page now names ' +
                'the real state, and a person has to settle the two books.',
            );
          } else if ((outcomes.legacy ?? 0) > 0) {
            // The one outcome that is NOT self-healing: say so plainly rather than
            // showing a success and leaving the trade blocked.
            addToast(
              'error',
              'This posting predates BackOffice request ids — check it in ' +
                'BackOffice; an administrator has to release the guard.',
            );
          } else if ((outcomes.unblocked ?? 0) > 0) {
            addToast(
              'success',
              'Posting guard cleared — BackOffice is asked again on the next ' +
                'scheduled run, under the same request id.',
            );
          } else if ((outcomes.recorded ?? 0) > 0) {
            addToast('success', 'Already recorded in the book — nothing was stuck.');
          } else {
            addToast('success', 'BackOffice posting reconciled.');
          }
        },
      },
    );
  };

  // LR-706 (g). Unlike every other action on this page it does not merely unblock
  // something: it writes `Transaction` rows and moves the trade's status, from
  // figures BackOffice returned. `uuids` are what the operator saw in the dialog —
  // the server reads BackOffice again and refuses if the set moved in between.
  const confirmRecordFromBackoffice = (uuids: string[]) => {
    if (recordFromBackoffice.isPending || uuids.length === 0) return;
    recordFromBackoffice.mutate(
      { id: trade.id, uuids },
      {
        onSuccess: (result) => {
          setRecordFromBoOpen(false);
          handleRefresh();
          addToast(
            'success',
            `Recorded ${result.transactions_created} BackOffice transaction(s) — ` +
              'the books agree and the trade has moved on.',
          );
        },
      },
    );
  };

  // LR-711 round 4: the dialog's words now come from the same module as the
  // panel's and are exhaustive over the state union — a ternary on one state
  // described every other state, present and future, as "never confirmed".
  const reconcileCopy = reconcileDialogCopy(
    trade.bo_posting?.state ?? 'timed_out',
  );

  // LR-715. Same shape as `confirmReconcile` and a different failure mode: this one
  // CAN be refused for a reason the operator must see (403 without the admin
  // capability, 400 on a row someone else just closed), and the dialog shows it
  // through `error` rather than a toast, so the typed reason is not lost.
  const confirmSettleManually = () => {
    const reason = settleReason.trim();
    if (settleBoPosting.isPending || !reason) return;
    settleBoPosting.mutate(
      { id: trade.id, reason },
      {
        onSuccess: (result) => {
          setSettleManuallyOpen(false);
          setSettleReason('');
          handleRefresh();
          if ((result.still_blocked ?? []).length > 0) {
            // The row is closed, so the panel is gone — and with it the only place
            // that said this trade is held. Nothing else will tell them.
            addToast(
              'error',
              'Posting closed — but the guard is still holding this trade, so ' +
                'further postings are refused. An administrator has to release it ' +
                'separately.',
            );
          } else {
            addToast(
              'success',
              'Posting closed as reconciled by hand. The trade stops reporting it.',
            );
          }
        },
      },
    );
  };

  const confirmSettleCash = () => {
    if (settleCash.isPending) return;
    if (!canPerform('settle_cash')) {
      addToast('error', `This position changed — Settle ${collateralLeg} is no longer available.`);
      setSettleCashOpen(false);
      return;
    }
    settleCash.mutate({ id: trade.id }, { onSuccess: onActionSuccess(() => setSettleCashOpen(false)) });
  };

  const confirmSettleSecurities = () => {
    if (settleSecurities.isPending) return;
    if (!canPerform('settle_securities')) {
      addToast('error', 'This position changed — Settle Securities is no longer available.');
      setSettleSecuritiesOpen(false);
      return;
    }
    settleSecurities.mutate({ id: trade.id }, { onSuccess: onActionSuccess(() => setSettleSecuritiesOpen(false)) });
  };

  // LR-478: scoped "View in MTM" → the MTM page filtered to THIS trade's
  // counterparty (not the alphabetical default).
  const handleViewInMtm = () => {
    // The trade's own book decides the workspace (LR-587).
    navigate({ to: '/collateral', search: collateralSearchForTrade(trade, trade.counterparty_id) });
  };

  // LR-478 (single-trade nav redesign): navigation affordances folded into the
  // toolbar's ··· overflow so they no longer compete with the lifecycle verbs.
  // They stay PAGE-OWNED (TradeActionButtons knows nothing about MTM, keeping the
  // Phase-13 guard green) and reachable even on terminal trades. View-in-MTM is
  // gated on MTM-visibility (active/matured); Mark history outlives close.
  const navItems: TradeNavItem[] = [
    ...(canViewMtm
      ? [{ key: 'view-mtm', label: 'View in MTM', icon: ChartBarIcon, onClick: handleViewInMtm }]
      : []),
    { key: 'mark-history', label: 'Mark history', icon: ClockIcon, onClick: () => setMarkHistoryOpen(true) },
  ];

  // LR-478: the single, always-mounted action surface (lives in the sticky
  // header). Stateless — modal state + mutations live below at the page level —
  // and always rendered full/expanded; navItems (View in MTM / Mark history) sit
  // inline alongside the lifecycle actions.
  // LR-544: "Open now" is out of scope for a future trade date (it opens
  // automatically on Trade Date). Disable the button with an explanatory
  // tooltip; the backend rejects the request too (defense-in-depth).
  const openNowDisabledReason = isTradeDateInFuture(trade.trade_date)
    ? `Available on Trade Date ${formatDate(trade.trade_date)}`
    : undefined;

  const tradeActionBar = (
    <TradeActionButtons
      validActions={validActions}
      onGiveNotice={() => setNoticeOpen(true)}
      onSetSettlementDate={() => setSettlementDateOpen(true)}
      onSettle={() => setSettleOpen(true)}
      onSettleCash={() => setSettleCashOpen(true)}
      onSettleSecurities={() => setSettleSecuritiesOpen(true)}
      onTerminate={() => setTerminateOpen(true)}
      onPartialRecall={() => setPartialRecallOpen(true)}
      onAmend={() => setAmendOpen(true)}
      onProcessTradeDate={() => setProcessTradeDateOpen(true)}
      openNowDisabledReason={openNowDisabledReason}
      onReRate={() => setReRateOpen(true)}
      onAssignCashPool={canTrade ? () => setAssignCashPoolOpen(true) : undefined}
      partialRecallLabel={trade?.trade_type === 'borrow' ? 'Partial Return' : 'Partial Recall'}
      settleCashLabel={`Settle ${collateralLeg}`}
      navItems={navItems}
    />
  );

  // LR-470: the Recalls tab is conditional on the trade actually having booked
  // recalls. Settled/cancelled rows stay in partial_recalls as history, so they
  // keep the tab visible — the fallback only fires when there are no booked rows
  // at all (none ever booked, or a 'recalls' key left in storage from a trade
  // that had them). A persisted 'recalls' for such a trade would otherwise select
  // a hidden, unmounted tab — fall back to Economics so the page never renders a
  // blank panel.
  const hasRecalls = (trade.partial_recalls?.length ?? 0) > 0;
  const effectiveTab = activeTab === 'recalls' && !hasRecalls ? 'analytics' : activeTab;

  return (
    <div className="relative flex h-full flex-col">
      {/* LR-478: the app shell is min-h-screen — the WINDOW scrolls, not an inner
          container (the old flex-1/overflow-auto never engaged, so the Phase-15
          IntersectionObserver condensed bar never fired). The header instead
          sticks to the viewport top via position:sticky; no condensed bar. */}
      <div className="bg-lr-bg">
        {/* Step 3: identity header (TRD-id, status, direction, ticker · counterparty
            · agreement, actions). Risk figures live in the KPI strip below. It is
            the single, always-mounted action surface (sticky to the viewport top),
            so a11y/tab order traverses the actions exactly once. */}
        <TradeHeader
          trade={trade}
          onRefresh={handleRefresh}
          actionSlot={tradeActionBar}
        />

        {/* LR-544: explainability for the Pending Trade Date state — a red
            banner (BO retries / rejected) or a neutral plaque ("booking on
            Trade Date; Settle appears after the BO hold; settlement date via
            Amend"). Renders null for every other status. */}
        <TradePendingBanner trade={trade} onOpenAmend={() => setAmendOpen(true)} />

        {/* LR-711: a BackOffice posting that failed or never confirmed. Sits beside
            the pending banner because it answers the same question — "why is this
            trade not moving?" — for the case the banner cannot see: a stall that
            has nothing to do with retries or the Pending status, and that until now
            was visible only in Django-admin. Renders nothing when BackOffice's last
            word was clean, which is almost always. */}
        {trade.bo_posting && (
          <div className="mx-auto max-w-[1440px] px-6 pt-4">
            <BoPostingPanel
              posting={trade.bo_posting}
              onReconcile={canTrade ? () => setReconcileOpen(true) : undefined}
              isReconciling={reconcileBoPosting.isPending}
              // LR-715: admin-only, mirroring the server's gate. Everyone still
              // reads what happened — the panel says BackOffice may hold the
              // posting twice either way; only the closing is an admin's call.
              onSettleManually={canAdmin ? () => setSettleManuallyOpen(true) : undefined}
              isSettling={settleBoPosting.isPending}
              // LR-706 (g): the same gate for the same reason — everyone reads that
              // BackOffice holds the posting; only writing our side from it is an
              // admin's call.
              onRecordFromBackoffice={
                canAdmin ? () => setRecordFromBoOpen(true) : undefined
              }
            />
          </div>
        )}

        {/* Step 2: number-priority KPI strip — hero pair (Net P&L/day, Rate) +
            secondary triptych (Exposure, Mark vs booking, Adequacy). Scrolls under
            the sticky header. */}
        <div data-tour="trade-detail-kpi-strip" className="mx-auto max-w-[1440px] px-6 pt-4 pb-1">
          <KpiStrip trade={trade} accruals={accruals} isLoading={accrualsLoading} isError={accrualsError} />
        </div>

        {/* Tab nav sits immediately below the strip (UI-SPEC §1) directly on the
            canvas — no white band (prototype `.tabs` has no background); the
            tablist's own bottom hairline is the divider to the panels. */}
        <div data-tour="trade-detail-tab-nav" className="mx-auto max-w-[1440px] px-6 pt-3">
          <TradeTabNavigation activeTab={effectiveTab} onTabChange={setActiveTab} showRecalls={hasRecalls} />
        </div>

        {/* Tab content — each tab is self-contained and renders its own state.
            Each active panel uses role="tabpanel" + aria-labelledby to complete
            the ARIA tabs relationship (WCAG 4.1.2). Non-active panels are
            conditionally unmounted (not just hidden) so their content stays out of
            the accessible tree; correspondingly, TradeTabNavigation emits
            aria-controls ONLY on the active tab button (WR-04) so it never points
            at an unmounted panel id. */}
        <div data-tour="trade-detail-tab-content" className="mx-auto max-w-[1440px] px-6 py-6">
          {effectiveTab === 'analytics' && (
            <div id="tab-panel-analytics" role="tabpanel" aria-labelledby="tab-analytics">
              <AnalyticsTab trade={trade} />
            </div>
          )}
          {effectiveTab === 'activity' && (
            <div id="tab-panel-activity" role="tabpanel" aria-labelledby="tab-activity">
              <ActivityTab tradeId={id} trade={trade} />
            </div>
          )}
          {effectiveTab === 'trade-terms' && (
            <div id="tab-panel-trade-terms" role="tabpanel" aria-labelledby="tab-trade-terms">
              <TradeTermsTab trade={trade} />
            </div>
          )}
          {/* LR-470: booked partial recalls/returns + their operator controls
              (settle / cancel / override settlement date). Conditional tab —
              only mounted when the trade has recalls. */}
          {effectiveTab === 'recalls' && hasRecalls && (
            <div id="tab-panel-recalls" role="tabpanel" aria-labelledby="tab-recalls">
              <PartialRecallsPanel trade={trade} />
            </div>
          )}
        </div>
      </div>

      {/* Modals — ONE instance each, hosted at the page level (LR-208 r3 H2): the
          stateless action buttons that toggle these flags live in the sticky
          header, so an open dialog is independent of the toolbar's lifecycle. */}
      <NoticeModal
        isOpen={noticeOpen}
        onClose={() => { setNoticeOpen(false); handleRefresh(); }}
        trade={trade}
        giveNotice={giveNotice}
      />
      <SettlementDateModal
        isOpen={settlementDateOpen}
        onClose={() => setSettlementDateOpen(false)}
        trade={trade}
        onSuccess={() => handleRefresh()}
        setSettlementDate={setSettlementDate}
        holidays={holidays}
      />
      {/* LR-544: manual "Open now" — fire the Trade Date action. The mutation
          invalidates the ['positions'] prefix; onSuccess=handleRefresh also
          refetches the activity timeline + accruals, for parity with the other
          modals (kvi P9). */}
      <ProcessTradeDateModal
        isOpen={processTradeDateOpen}
        onClose={() => setProcessTradeDateOpen(false)}
        trade={trade}
        processTradeDate={processTradeDate}
        onSuccess={() => handleRefresh()}
      />
      {/* LR-711: reconcile a posting whose outcome was never learned. Safe by
          construction — it re-sends under the same request id, so BackOffice books
          it only if it has not already — but it can clear a guard and move the
          trade, so it goes through the same confirm rail as every other mutation
          here rather than firing from the panel directly. */}
      <ConfirmDialog
        show={reconcileOpen}
        onClose={() => setReconcileOpen(false)}
        title="Reconcile with BackOffice"
        confirmLabel="Reconcile"
        confirmColor="blue"
        isLoading={reconcileBoPosting.isPending}
        onConfirm={confirmReconcile}
        error={reconcileBoPosting.error}
        errorFallback="Failed to reconcile the posting. Please try again."
        showResponseDetail
      >
        <p className="text-sm text-gray-500">
          Position <strong>{trade.id}</strong> ({trade.symbol_id}){' '}
          {reconcileCopy.lead}. {reconcileCopy.effect}
        </p>
        <p className="mt-2 text-sm text-gray-500">
          A posting made before request ids were introduced cannot be recognised by
          BackOffice, and will be reported back as still needing a person.
        </p>
      </ConfirmDialog>
      {/* LR-706 (g): the legacy posting's own exit. Its body is fetched, because
          the operator is approving ledger rows and the only honest way to ask is to
          show them what will be written. */}
      <RecordFromBackofficeDialog
        show={recordFromBoOpen}
        tradeId={trade.id}
        onClose={() => setRecordFromBoOpen(false)}
        onConfirm={confirmRecordFromBackoffice}
        isRecording={recordFromBackoffice.isPending}
        recordError={recordFromBackoffice.error}
      />

      {/* LR-715: close a posting BackOffice contradicted itself about. The reason is
          mandatory and the confirm stays disabled without one — unlike every other
          dialog here, no answer from BackOffice stands behind this settlement, so
          that sentence is the whole record of why the row was closed. The textarea
          sits inside the ConfirmDialog the way SetCloseDateDialog's does; the
          focus-trap problem AmendTradeModal documents is a STACKED-dialog one, and
          this dialog opens from the page. */}
      <ConfirmDialog
        show={settleManuallyOpen}
        onClose={() => {
          setSettleManuallyOpen(false);
          setSettleReason('');
        }}
        title={SETTLE_MANUALLY_COPY.dialogTitle}
        titleColor="red"
        confirmLabel="Close posting"
        confirmColor="red"
        isLoading={settleBoPosting.isPending}
        confirmDisabled={!settleReason.trim()}
        onConfirm={confirmSettleManually}
        error={settleBoPosting.error}
        errorFallback="Failed to close the posting. Please try again."
        showResponseDetail
      >
        <p className="text-sm text-gray-500">
          Position <strong>{trade.id}</strong> ({trade.symbol_id}){' '}
          {SETTLE_MANUALLY_COPY.dialogLead}. {SETTLE_MANUALLY_COPY.dialogEffect}
        </p>
        <div className="mt-4">
          <label
            htmlFor="bo-posting-settle-reason"
            className="block text-xs text-gray-500 mb-1"
          >
            {SETTLE_MANUALLY_COPY.reasonLabel}
          </label>
          <textarea
            id="bo-posting-settle-reason"
            value={settleReason}
            onChange={(e) => setSettleReason(e.target.value)}
            placeholder={SETTLE_MANUALLY_COPY.reasonPlaceholder}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-red-500 focus:border-red-500"
            rows={3}
          />
        </div>
      </ConfirmDialog>
      <ConfirmDialog
        show={settleOpen}
        onClose={() => setSettleOpen(false)}
        title="Settle Position"
        confirmLabel="Settle"
        confirmColor="green"
        isLoading={settle.isPending}
        onConfirm={confirmSettle}
        error={settle.error}
        errorFallback="Failed to settle position. Please try again."
        // LR-581: the BO reason (400 refusal / 502 outage) arrives in `detail` —
        // show it verbatim instead of the capped generic line.
        showResponseDetail
      >
        <p className="text-sm text-gray-500">
          Are you sure you want to settle position <strong>{trade.id}</strong> ({trade.symbol_id})?
          This will create backoffice transactions and activate the position.
        </p>
      </ConfirmDialog>
      <ConfirmDialog
        show={settleCashOpen}
        onClose={() => setSettleCashOpen(false)}
        title={`Settle ${collateralLeg} Leg`}
        confirmLabel={`Settle ${collateralLeg}`}
        confirmColor="green"
        isLoading={settleCash.isPending}
        onConfirm={confirmSettleCash}
        error={settleCash.error}
        errorFallback={`Failed to settle ${collateralLeg.toLowerCase()} leg.`}
        showResponseDetail
      >
        <p className="text-sm text-gray-500">
          Settle {collateralLeg.toLowerCase()} leg of position <strong>{trade.id}</strong> ({trade.symbol_id}).
          Securities leg will remain pending until settled separately.
        </p>
      </ConfirmDialog>
      <ConfirmDialog
        show={settleSecuritiesOpen}
        onClose={() => setSettleSecuritiesOpen(false)}
        title="Settle Securities Leg"
        confirmLabel="Settle Securities"
        confirmColor="green"
        isLoading={settleSecurities.isPending}
        onConfirm={confirmSettleSecurities}
        error={settleSecurities.error}
        errorFallback="Failed to settle securities leg."
        showResponseDetail
      >
        <p className="text-sm text-gray-500">
          Settle securities leg of position <strong>{trade.id}</strong> ({trade.symbol_id}).
          {collateralLeg} leg will remain pending until settled separately.
        </p>
      </ConfirmDialog>
      <TerminateModal
        isOpen={terminateOpen}
        onClose={() => { setTerminateOpen(false); handleRefresh(); }}
        trade={trade}
        terminate={terminate}
      />
      <PartialRecallModal
        isOpen={partialRecallOpen}
        // No handleRefresh() here: usePartialRecall already invalidates tradesKeys.all
        // (['positions']) on success, which refetches detail + list. Calling it on close
        // would also refetch on Cancel — a wasted round-trip for a no-op (dp review #3).
        onClose={() => setPartialRecallOpen(false)}
        trade={trade}
        partialRecall={partialRecall}
      />
      {/* LR-484: amend a pending trade's NUMBERS via the reused trade-entry form.
          Mounted only while open — it reuses the whole booking form (useAmendTradeForm
          pulls the registry/market hooks), so there's no reason to run it while closed.
          Cache invalidation lives in useUpdatePosition, so no handleRefresh here. */}
      {amendOpen && (
        <AmendTradeModal
          isOpen={amendOpen}
          onClose={() => setAmendOpen(false)}
          trade={trade}
          updatePosition={updatePosition}
        />
      )}
      {/* LR-528: re-rate the rate leg of an active / backdated matured trade.
          Mounted only while open. Cache invalidation lives in useReRate (positions
          + accruals prefixes), so no handleRefresh here. */}
      {reRateOpen && (
        <ReRateModal
          isOpen={reRateOpen}
          onClose={() => setReRateOpen(false)}
          trade={trade}
          reRate={reRate}
        />
      )}
      {assignCashPoolOpen && (
        <AssignCashPoolModal
          isOpen={assignCashPoolOpen}
          onClose={() => setAssignCashPoolOpen(false)}
          trade={trade}
        />
      )}
      {/* LR-478: "Mark history" peek — the trade's marks shown in-place (the MTM
          drawer accepts a structural subset, so PositionDetail fits). Passing
          null when closed drives the drawer's own leave transition. */}
      <MtmDetailsDrawer
        trade={markHistoryOpen ? trade : null}
        onClose={() => setMarkHistoryOpen(false)}
      />
    </div>
  );
}
